import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, ArrowRight, Lock, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useAuthStore } from '../stores/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, login, checkAuth } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);
  
  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Left Pane: Login Controls */}
      <div className="w-full lg:w-1/2 bg-white flex flex-col justify-between p-8 sm:p-12 lg:p-16">
        <div>
          {/* Brand Logo */}
          <Link to="/" className="inline-flex items-center gap-2.5 mb-10 group">
            <img src="/logo.png" alt="InsuScan AI" className="h-9 w-9 object-contain" />
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight text-slate-900 group-hover:text-blue-600 transition-colors">
                InsuScan <span className="text-blue-600">AI</span>
              </span>
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 -mt-1">
                Asset Integrity Console
              </span>
            </div>
          </Link>

          {/* Form Header */}
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse" />
              <span>ENTERPRISE OPERATOR GATEWAY</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mb-2">
              Sign In to Your Workspace
            </h1>
            <p className="text-sm text-slate-600 mb-8">
              Access calibrated NDT screening models, deterministic API 579-1 calculations, and CMMS integration.
            </p>

            {/* Login Card Actions */}
            <div className="space-y-4">
              <button
                onClick={login}
                className="btn btn-primary w-full py-3.5 px-5 text-base font-semibold shadow-md flex items-center justify-center gap-2"
              >
                <Lock className="h-5 w-5" />
                <span>Authenticate with Logto OIDC</span>
                <ArrowRight className="h-5 w-5 ml-auto" />
              </button>

              <button
                onClick={login}
                className="btn btn-secondary w-full py-3 px-5 text-sm font-semibold flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50"
              >
                <span>Enter Demo Operator Mode</span>
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* OIDC Information */}
            <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-2">
              <div className="flex items-center gap-2 font-bold text-slate-800">
                <Shield className="h-4 w-4 text-blue-600" />
                <span>Zero-Trust Enterprise Protocol</span>
              </div>
              <p className="leading-relaxed">
                Single Sign-On (SSO) compliant with OpenID Connect, SAML 2.0, Azure AD, and Okta via PKCE. 
                Industrial data remains encrypted in transit and at rest according to ISO 27001 standards.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-8 border-t border-slate-200 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} InsuScan AI Inc.</span>
          <div className="flex gap-4">
            <Link to="/docs" className="hover:text-slate-800">Documentation</Link>
            <Link to="/faq" className="hover:text-slate-800">FAQ</Link>
          </div>
        </div>
      </div>

      {/* Right Pane: Visual Showcase with Banner Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950 flex-col justify-between p-12 text-white">
        <img
          src="/banner.png"
          alt="InsuScan AI Field Intelligence"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40 mix-blend-luminosity scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />

        {/* Top Telemetry Overlay */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-xs text-slate-300">
            <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
            <span>KERNEL: v2.4-DETERMINISTIC</span>
          </div>
          <span className="telemetry-pill bg-slate-800/80 text-blue-300 border border-slate-700">
            API 579-1 / ASME FFS-1
          </span>
        </div>

        {/* Center Technical Callout */}
        <div className="relative z-10 max-w-lg space-y-6">
          <div className="space-y-2">
            <span className="font-mono text-xs font-semibold text-amber-400 tracking-wider uppercase">
              // ON-STREAM INTEGRITY INTELLIGENCE
            </span>
            <h2 className="text-3xl font-extrabold tracking-tight text-white leading-tight">
              Eliminate Scaffolding Bottlenecks in Process Plants
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed">
              "By screening piping circuits on-stream with radiometric thermography and automated AI jacketing segmentation, 
              we prevented over €800,000 in unnecessary insulation stripping."
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 font-mono text-xs pt-2">
            <div className="p-3 rounded-lg bg-slate-900/80 border border-slate-800">
              <div className="text-slate-400 text-[10px]">VERIFIED UNNECESSARY STRIP RATE</div>
              <div className="text-xl font-bold text-amber-400 mt-1">60% – 80%</div>
              <div className="text-slate-400 text-[10px] mt-0.5">Dry insulation saved</div>
            </div>
            <div className="p-3 rounded-lg bg-slate-900/80 border border-slate-800">
              <div className="text-slate-400 text-[10px]">TIME TO EVALUATE FFS LEVEL 1</div>
              <div className="text-xl font-bold text-green-400 mt-1">&lt; 120 Seconds</div>
              <div className="text-slate-400 text-[10px] mt-0.5">Automated RSF disposition</div>
            </div>
          </div>
        </div>

        {/* Bottom Feature Badges */}
        <div className="relative z-10 pt-6 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400 font-mono">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-blue-400" />
            <span>SAP PM & IBM MAXIMO</span>
          </span>
          <span className="flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4 text-blue-400" />
            <span>API RP 583 CUI ZONE</span>
          </span>
        </div>
      </div>
    </div>
  );
}
